import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LoggerService } from './logger.service';
import { ErrorHandlerService } from './error-handler.service';
import { TIMING } from '../constants/app.constants';

export interface BackgroundOption {
  id: string;
  name: string;
  imagePath: string;
  previewPath: string;
  type: 'image' | 'color';
  colorValue?: string; // Für Farben
}

@Injectable({
  providedIn: 'root'
})
export class BackgroundSettingsService {
  private logger = inject(LoggerService);
  private errorHandler = inject(ErrorHandlerService);

  private readonly STORAGE_KEY = 'app_background_setting';
  private readonly DEFAULT_BACKGROUND = 'bg1';
  
  // Import timing constant
  private readonly ANIMATION_DURATION = TIMING.ANIMATION_DURATION;
  
  private currentBackgroundSubject = new BehaviorSubject<string>(this.DEFAULT_BACKGROUND);
  public currentBackground$ = this.currentBackgroundSubject.asObservable();
  
  // Track setTimeout for cleanup
  private animationTimeout: ReturnType<typeof setTimeout> | null = null;

  // Available background options
  public readonly backgroundOptions: BackgroundOption[] = [
    // Hintergrundbilder
    { id: 'bg1', name: 'Background 1', imagePath: '/assets/backgrounds/bg1.png', previewPath: '/assets/backgrounds/bg1.png', type: 'image' },
    { id: 'bg2', name: 'Background 2', imagePath: '/assets/backgrounds/bg2.png', previewPath: '/assets/backgrounds/bg2.png', type: 'image' },
    { id: 'bg3', name: 'Background 3', imagePath: '/assets/backgrounds/bg3.png', previewPath: '/assets/backgrounds/bg3.png', type: 'image' },
    { id: 'bg4', name: 'Background 4', imagePath: '/assets/backgrounds/bg4.png', previewPath: '/assets/backgrounds/bg4.png', type: 'image' },
    { id: 'bg5', name: 'Background 5', imagePath: '/assets/backgrounds/bg5.png', previewPath: '/assets/backgrounds/bg5.png', type: 'image' },
    // Hintergrundfarben
    { id: 'color1', name: 'Dark Gray', imagePath: '', previewPath: '', type: 'color', colorValue: '#626162' },
    { id: 'color2', name: 'Dark Green', imagePath: '', previewPath: '', type: 'color', colorValue: '#018452' },
    { id: 'color3', name: 'Dark Purple', imagePath: '', previewPath: '', type: 'color', colorValue: '#3E0290' },
    { id: 'color4', name: 'Dark Teal', imagePath: '', previewPath: '', type: 'color', colorValue: '#016589' }
  ];

  /**
   * Gets the current background ID
   */
  getCurrentBackground(): string {
    return this.currentBackgroundSubject.value;
  }

  /**
   * Sets a new background
   * Note: Background is now stored in userData.app_bg (via API), not in separate localStorage
   */
  setBackground(backgroundId: string): void {
    const backgroundOption = this.backgroundOptions.find(bg => bg.id === backgroundId);
    if (backgroundOption) {
      this.currentBackgroundSubject.next(backgroundId);
      // Background is saved via API in userData.app_bg, no need for separate localStorage entry
      // Remove old localStorage entry if it exists (migration)
      localStorage.removeItem(this.STORAGE_KEY);
      this.applyBackgroundToGlobalCSS(backgroundOption.imagePath, backgroundOption.colorValue);
    }
  }

  /**
   * Sets background immediately without animation (for initial load)
   * Use this when setting background before navigation to avoid visible animation delays
   */
  setBackgroundImmediate(backgroundId: string): void {
    const backgroundOption = this.backgroundOptions.find(bg => bg.id === backgroundId);
    if (backgroundOption) {
      this.currentBackgroundSubject.next(backgroundId);
      // Set CSS variables immediately without animation
      if (backgroundOption.colorValue) {
        document.documentElement.style.setProperty('--app-background-image', 'none');
        document.documentElement.style.setProperty('--app-background-color', backgroundOption.colorValue);
      } else {
        document.documentElement.style.setProperty('--app-background-image', `url('${backgroundOption.imagePath}')`);
        document.documentElement.style.setProperty('--app-background-color', 'transparent');
      }
    }
  }

  /**
   * Sets all styles needed before navigation to home page
   * This includes background and header/toolbar heights to prevent style updates during navigation animation
   */
  setStylesBeforeNavigation(backgroundId: string): void {
    // Set background immediately
    this.setBackgroundImmediate(backgroundId);
    
    // Set header and toolbar heights to prevent style updates during navigation
    // These values match the expected heights based on CSS (min-height: 44px + padding: 18px top + 18px bottom = 80px)
    document.documentElement.style.setProperty('--ion-header-height', '80px');
    document.documentElement.style.setProperty('--ion-toolbar-height', '80px');
  }

  /**
   * Gets the background option by ID
   */
  getBackgroundOption(backgroundId: string): BackgroundOption | undefined {
    return this.backgroundOptions.find(bg => bg.id === backgroundId);
  }

  /**
   * Loads background setting from localStorage
   * Note: Background is primarily stored in userData.app_bg (via API)
   * This method loads from localStorage as fallback/migration support
   * AuthService will call setBackground() when userData is loaded
   */
  private loadBackgroundSetting(): void {
    try {
      // Try old localStorage entry (for migration/fallback)
      const savedBackground = localStorage.getItem(this.STORAGE_KEY);
      
      if (savedBackground && this.backgroundOptions.some(bg => bg.id === savedBackground)) {
        this.currentBackgroundSubject.next(savedBackground);
        const backgroundOption = this.getBackgroundOption(savedBackground);
        if (backgroundOption) {
          this.applyBackgroundToGlobalCSS(backgroundOption.imagePath, backgroundOption.colorValue);
        }
        // Remove old entry after reading (migration cleanup)
        localStorage.removeItem(this.STORAGE_KEY);
      } else {
        // Apply default background
        const defaultOption = this.getBackgroundOption(this.DEFAULT_BACKGROUND);
        if (defaultOption) {
          this.applyBackgroundToGlobalCSS(defaultOption.imagePath, defaultOption.colorValue);
        }
      }
    } catch (error) {
      this.errorHandler.handleError('Background-Load', error);
      // Apply default background on error
      const defaultOption = this.getBackgroundOption(this.DEFAULT_BACKGROUND);
      if (defaultOption) {
        this.applyBackgroundToGlobalCSS(defaultOption.imagePath, defaultOption.colorValue);
      }
    }
  }

  /**
   * Note: Background is now saved via API in userData.app_bg
   * This method is kept for backward compatibility but does nothing
   * (background is saved through AuthService.updateUserData)
   */
  private saveBackgroundSetting(backgroundId: string): void {
    // Background is now stored in userData.app_bg via API, not in separate localStorage
    // This method is kept for backward compatibility but does nothing
  }

  /**
   * Applies the background to global CSS variable with animation
   */
  private applyBackgroundToGlobalCSS(imagePath: string, colorValue?: string): void {
    // Clear any existing timeout to prevent overlapping animations
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
    
    // Fade-out Animation
    this.fadeOutBackground();
    
    // Nach der Fade-out Animation die neuen Werte setzen
    this.animationTimeout = setTimeout(() => {
      if (colorValue) {
        // Für Farben: Setze sowohl Hintergrundfarbe als auch entferne Hintergrundbild
        document.documentElement.style.setProperty('--app-background-image', 'none');
        document.documentElement.style.setProperty('--app-background-color', colorValue);
      } else {
        // Für Bilder: Setze Hintergrundbild und entferne Hintergrundfarbe
        document.documentElement.style.setProperty('--app-background-image', `url('${imagePath}')`);
        document.documentElement.style.setProperty('--app-background-color', 'transparent');
      }
      
      // Fade-in Animation
      this.fadeInBackground();
      
      // Clear timeout reference after completion
      this.animationTimeout = null;
    }, this.ANIMATION_DURATION);
  }
  
  /**
   * Cleanup method to clear any pending timeouts
   * Should be called when service is no longer needed (e.g., app shutdown)
   */
  cleanup(): void {
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
  }

  /**
   * Fade-out animation for background
   */
  private fadeOutBackground(): void {
    const contentElements = document.querySelectorAll('ion-content');
    contentElements.forEach(element => {
      const backgroundPart = element.shadowRoot?.querySelector('[part="background"]') as HTMLElement;
      if (backgroundPart) {
        backgroundPart.style.opacity = '0.3';
        backgroundPart.style.transition = 'opacity 0.4s ease-in-out';
      }
    });
  }

  /**
   * Fade-in animation for background
   */
  private fadeInBackground(): void {
    const contentElements = document.querySelectorAll('ion-content');
    contentElements.forEach(element => {
      const backgroundPart = element.shadowRoot?.querySelector('[part="background"]') as HTMLElement;
      if (backgroundPart) {
        backgroundPart.style.opacity = '1';
        backgroundPart.style.transition = 'opacity 0.4s ease-in-out';
      }
    });
  }

  /**
   * Setzt den Hintergrund auf den Standardwert zurück (für Logout)
   * Verwendet Animation für sanften Übergang beim Logout
   */
  resetToDefault(): void {
    this.setBackground(this.DEFAULT_BACKGROUND);
  }

  /**
   * Setzt den Hintergrund auf den Standardwert zurück ohne Animation (für Page Reload)
   * Verwendet setStylesBeforeNavigation() um Animation zu vermeiden
   */
  resetToDefaultImmediate(): void {
    this.setStylesBeforeNavigation(this.DEFAULT_BACKGROUND);
  }
}
